import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

export default function AdminBackup() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(null);
  const [createData, setCreateData] = useState({ backup_type: 'database', note: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadConfirm, setShowUploadConfirm] = useState(false);

  const fetchBackups = () => {
    setLoading(true);
    api.get('/admin/backups/')
      .then(res => setBackups(res.data.results || res.data))
      .catch(() => toast.error('Failed to load backups.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBackups(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await api.post('/admin/backups/create/', createData);
      toast.success(`${res.data.backup_type_display} backup created successfully!`);
      setShowCreateModal(false);
      setCreateData({ backup_type: 'database', note: '' });
      fetchBackups();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create backup.');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (backup) => {
    try {
      const res = await api.get(`/admin/backups/${backup.id}/download/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      // Extract filename from file_path
      const filename = backup.file_path?.split(/[/\\]/).pop() || `backup_${backup.id}.zip`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download backup.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this backup?')) return;
    setDeletingId(id);
    try {
      await api.delete(`/admin/backups/${id}/delete/`);
      toast.success('Backup deleted.');
      fetchBackups();
    } catch {
      toast.error('Failed to delete backup.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleRestore = async (id) => {
    setRestoringId(id);
    try {
      const res = await api.post(`/admin/backups/${id}/restore/`);
      toast.success(res.data.detail || 'Database restored successfully!');
      setShowRestoreConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed.');
    } finally {
      setRestoringId(null);
    }
  };

  const handleUploadRestore = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const res = await api.post('/admin/backups/upload-restore/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      });
      toast.success(res.data.detail || 'Database restored successfully from uploaded file!');
      setUploadFile(null);
      setShowUploadConfirm(false);
      fetchBackups();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload restore failed.');
    } finally {
      setUploading(false);
    }
  };

  const getTypeIcon = (type) => {
    if (type === 'database') return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
      </svg>
    );
    if (type === 'media') return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
      </svg>
    );
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
      </svg>
    );
  };

  const getTypeBadge = (type) => {
    const styles = {
      database: 'bg-blue-100 text-blue-700',
      media: 'bg-purple-100 text-purple-700',
      full: 'bg-green-100 text-green-700',
    };
    const labels = { database: 'Database', media: 'Media', full: 'Full' };
    return (
      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${styles[type] || 'bg-gray-100 text-gray-700'}`}>
        {getTypeIcon(type)}
        {labels[type] || type}
      </span>
    );
  };

  const getStatusBadge = (s) => {
    if (s === 'completed') return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Completed</span>;
    if (s === 'in_progress') return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">In Progress</span>;
    return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Failed</span>;
  };

  const completedCount = backups.filter(b => b.status === 'completed').length;
  const totalSize = backups.filter(b => b.status === 'completed').reduce((s, b) => s + (b.file_size || 0), 0);
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Backup Management</h1>
            <p className="text-sm text-gray-500 mt-1">Create, download, and restore system backups</p>
          </div>
          <button onClick={() => setShowCreateModal(true)}
            className="bg-[#0B5D1E] text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Backup
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{backups.length}</p>
              <p className="text-xs text-gray-500">Total Backups</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{completedCount}</p>
              <p className="text-xs text-gray-500">Successful</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{formatSize(totalSize)}</p>
              <p className="text-xs text-gray-500">Total Storage Used</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <button
          onClick={() => { setCreateData({ backup_type: 'database', note: '' }); setShowCreateModal(true); }}
          className="bg-white rounded-xl shadow-sm border p-5 hover:border-blue-300 hover:shadow-md transition text-left group"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Database Backup</h3>
              <p className="text-xs text-gray-500">Export MySQL database</p>
            </div>
          </div>
        </button>
        <button
          onClick={() => { setCreateData({ backup_type: 'media', note: '' }); setShowCreateModal(true); }}
          className="bg-white rounded-xl shadow-sm border p-5 hover:border-purple-300 hover:shadow-md transition text-left group"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center group-hover:bg-purple-200 transition">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Media Backup</h3>
              <p className="text-xs text-gray-500">Export uploaded files</p>
            </div>
          </div>
        </button>
        <button
          onClick={() => { setCreateData({ backup_type: 'full', note: '' }); setShowCreateModal(true); }}
          className="bg-white rounded-xl shadow-sm border p-5 hover:border-green-300 hover:shadow-md transition text-left group"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Full Backup</h3>
              <p className="text-xs text-gray-500">Database + media files</p>
            </div>
          </div>
        </button>
      </div>

      {/* Upload & Restore Section */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden mb-6">
        <div className="px-5 py-4 border-b bg-orange-50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-800">Upload & Restore Backup</h2>
              <p className="text-xs text-gray-500">Upload a previously downloaded backup .zip file to restore the database</p>
            </div>
          </div>
        </div>
        <div className="px-5 py-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1 w-full">
              <label className="block">
                <div className={`relative flex items-center gap-3 border-2 border-dashed rounded-xl px-4 py-4 transition cursor-pointer ${
                  uploadFile ? 'border-orange-300 bg-orange-50' : 'border-gray-300 hover:border-orange-300 hover:bg-orange-50/50'
                }`}>
                  <svg className={`w-8 h-8 flex-shrink-0 ${uploadFile ? 'text-orange-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="min-w-0">
                    {uploadFile ? (
                      <>
                        <p className="text-sm font-semibold text-gray-900 truncate">{uploadFile.name}</p>
                        <p className="text-xs text-gray-500">{(uploadFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-gray-700">Click to select a backup file</p>
                        <p className="text-xs text-gray-400">Only .zip files from previous backups are accepted</p>
                      </>
                    )}
                  </div>
                  {uploadFile && (
                    <button type="button" onClick={(e) => { e.preventDefault(); setUploadFile(null); }}
                      className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  <input type="file" accept=".zip" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    onChange={(e) => { if (e.target.files[0]) setUploadFile(e.target.files[0]); }}
                  />
                </div>
              </label>
            </div>
            <button
              onClick={() => setShowUploadConfirm(true)}
              disabled={!uploadFile || uploading}
              className="px-5 py-3 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Upload & Restore
            </button>
          </div>
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
            <p className="text-xs text-amber-800">
              <strong>For disaster recovery:</strong> If the server lost its backup files, upload a .zip file you previously downloaded to restore the database. Media-only backups cannot be restored this way.
            </p>
          </div>
        </div>
      </div>

      {/* Upload Restore Confirmation Modal */}
      {showUploadConfirm && uploadFile && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !uploading && setShowUploadConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Restore from Uploaded File</h3>
                <p className="text-xs text-gray-500">This action is irreversible</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> Restoring from this uploaded file will <strong>overwrite the entire current database</strong>.
              </p>
              <div className="mt-2 text-sm text-red-700 space-y-1">
                <p><strong>File:</strong> {uploadFile.name}</p>
                <p><strong>Size:</strong> {(uploadFile.size / (1024 * 1024)).toFixed(2)} MB</p>
              </div>
              <p className="mt-2 text-sm text-red-800 font-semibold">
                All current data will be permanently lost. Make sure this is the correct backup file.
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowUploadConfirm(false)} disabled={uploading}
                className="px-5 py-2 rounded-lg border text-sm font-semibold hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button
                onClick={handleUploadRestore}
                disabled={uploading}
                className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-2">
                {uploading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Restoring...
                  </>
                ) : 'Yes, Upload & Restore'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore from Backup Section */}
      {(() => {
        const restorableBackups = backups.filter(b => b.status === 'completed' && b.backup_type !== 'media');
        if (restorableBackups.length === 0) return null;
        return (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden mb-6">
            <div className="px-5 py-4 border-b bg-amber-50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-800">Restore from Backup</h2>
                  <p className="text-xs text-gray-500">Select a completed backup to restore the database</p>
                </div>
              </div>
            </div>
            <div className="divide-y">
              {restorableBackups.map(b => (
                <div key={b.id} className="px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition">
                  <div className="flex items-center gap-4">
                    {getTypeBadge(b.backup_type)}
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(b.created_at).toLocaleDateString()} — {new Date(b.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <p className="text-xs text-gray-500">
                        {b.file_size_display || '—'}{b.note ? ` · ${b.note}` : ''}{b.created_by_name ? ` · by ${b.created_by_name}` : ''}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowRestoreConfirm(b)}
                    disabled={!!restoringId}
                    className="px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition disabled:opacity-50 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Restore
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Backup History Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-800">Backup History</h2>
          <button onClick={fetchBackups}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">S/N</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Size</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Note</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Created By</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan="8" className="text-center py-12 text-gray-500">
                  <svg className="animate-spin h-6 w-6 text-gray-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  Loading backups...
                </td></tr>
              ) : backups.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-12">
                  <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                  <p className="text-sm text-gray-500 font-medium">No backups yet</p>
                  <p className="text-xs text-gray-400 mt-1">Create your first backup to get started</p>
                </td></tr>
              ) : backups.map((b, idx) => (
                <tr key={b.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                  <td className="px-4 py-3">{getTypeBadge(b.backup_type)}</td>
                  <td className="px-4 py-3">{getStatusBadge(b.status)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-700">{b.file_size_display || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">{b.note || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{b.created_by_name || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                    {new Date(b.created_at).toLocaleDateString()} {new Date(b.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      {b.status === 'completed' && (
                        <>
                          <button onClick={() => handleDownload(b)}
                            title="Download"
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3" />
                            </svg>
                          </button>
                          {b.backup_type !== 'media' && (
                            <button onClick={() => setShowRestoreConfirm(b)}
                              title="Restore"
                              className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </button>
                          )}
                        </>
                      )}
                      <button onClick={() => handleDelete(b.id)}
                        disabled={deletingId === b.id}
                        title="Delete"
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition disabled:opacity-50">
                        {deletingId === b.id ? (
                          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {backups.some(b => b.status === 'failed' && b.error_message) && (
          <div className="border-t px-5 py-3 bg-red-50">
            <p className="text-xs font-semibold text-red-700 mb-1">Error Details</p>
            {backups.filter(b => b.status === 'failed' && b.error_message).map(b => (
              <p key={b.id} className="text-xs text-red-600 mb-1">
                [{new Date(b.created_at).toLocaleDateString()}] {b.error_message}
              </p>
            ))}
          </div>
        )}
      </div>

      {/* Create Backup Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !creating && setShowCreateModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Create New Backup</h3>
                <p className="text-xs text-gray-500">Select backup type and add an optional note</p>
              </div>
            </div>

            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Backup Type</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: 'database', label: 'Database', desc: 'MySQL dump', color: 'blue' },
                      { value: 'media', label: 'Media', desc: 'Uploaded files', color: 'purple' },
                      { value: 'full', label: 'Full', desc: 'DB + Media', color: 'green' },
                    ].map(opt => (
                      <button key={opt.value} type="button"
                        onClick={() => setCreateData({ ...createData, backup_type: opt.value })}
                        className={`p-3 rounded-xl border-2 text-left transition ${
                          createData.backup_type === opt.value
                            ? `border-${opt.color}-500 bg-${opt.color}-50 ring-2 ring-${opt.color}-200`
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-2 h-2 rounded-full ${
                            createData.backup_type === opt.value ? `bg-${opt.color}-500` : 'bg-gray-300'
                          }`}></div>
                          <span className="text-sm font-semibold text-gray-900">{opt.label}</span>
                        </div>
                        <p className="text-xs text-gray-500 ml-4">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Note (optional)</label>
                  <input type="text" value={createData.note}
                    onChange={(e) => setCreateData({ ...createData, note: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                    placeholder="e.g. Before system update, Weekly backup..." />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                  <strong>Note:</strong> {createData.backup_type === 'full'
                    ? 'Full backups include the database and all uploaded media files. This may take several minutes for large datasets.'
                    : createData.backup_type === 'media'
                    ? 'Media backup includes all uploaded files (member photos, certificates, documents, etc.).'
                    : 'Database backup exports all tables, routines, and triggers from the MySQL database.'}
                </div>
              </div>

              <div className="flex gap-2 justify-end mt-5">
                <button type="button" onClick={() => setShowCreateModal(false)} disabled={creating}
                  className="px-5 py-2 rounded-lg border text-sm font-semibold hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                <button type="submit" disabled={creating}
                  className="px-5 py-2 rounded-lg bg-[#0B5D1E] text-white text-sm font-semibold hover:bg-[#062E10] transition disabled:opacity-50 flex items-center gap-2">
                  {creating ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      Creating Backup...
                    </>
                  ) : 'Create Backup'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !restoringId && setShowRestoreConfirm(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Restore Database</h3>
                <p className="text-xs text-gray-500">This action is irreversible</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> Restoring this backup will <strong>overwrite the entire current database</strong> with the data from:
              </p>
              <div className="mt-2 text-sm text-red-700 space-y-1">
                <p><strong>Type:</strong> {showRestoreConfirm.backup_type_display}</p>
                <p><strong>Date:</strong> {new Date(showRestoreConfirm.created_at).toLocaleString()}</p>
                <p><strong>Size:</strong> {showRestoreConfirm.file_size_display}</p>
                {showRestoreConfirm.note && <p><strong>Note:</strong> {showRestoreConfirm.note}</p>}
              </div>
              <p className="mt-2 text-sm text-red-800 font-semibold">
                All data created after this backup will be permanently lost. Consider creating a new backup first.
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowRestoreConfirm(null)} disabled={!!restoringId}
                className="px-5 py-2 rounded-lg border text-sm font-semibold hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button
                onClick={() => handleRestore(showRestoreConfirm.id)}
                disabled={!!restoringId}
                className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-2">
                {restoringId ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Restoring...
                  </>
                ) : 'Yes, Restore Database'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
